import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SOCKET_EVENTS } from '@tabbio/types';
import type {
  NotificationCountEvent,
  UnreadCountEvent,
} from '@tabbio/types';
import { setNotificationUnreadCount } from '../lib/notification-count';
import { QUERY_KEYS } from '../lib/query-keys';
import { useSocket } from './useSocket';

/**
 * App-level bridge from socket events into TanStack Query count caches.
 * Mount once in authenticated runtime; display hooks should only read queries.
 */
export function useUnreadCountSync() {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    // Reconnect intentionally does NOT invalidate unread caches: server
    // pushes UNREAD_COUNT / NOTIFICATION_COUNT events on read/new-msg, so a
    // REST refetch here would just add load. Conversations list does refresh
    // because socket only emits per-conversation updates, not whole lists.
    const handleConnect = () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    };

    const handleUnread = (event: UnreadCountEvent) => {
      // Server is authoritative for totalCount: write it straight into the
      // matching workspace cache to avoid a REST round-trip per event.
      if (event.workspace) {
        queryClient.setQueryData(
          [
            ...QUERY_KEYS.UNREAD_COUNT,
            event.workspace.mode,
            event.workspace.companyId ?? null,
          ],
          event.totalCount,
        );
      } else {
        // Legacy server payload without workspace tag: invalidate so the
        // sidebar badge still updates. Throttled at the API client layer.
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.UNREAD_COUNT,
        });
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
    };

    const handleNotificationCount = (event: NotificationCountEvent) => {
      setNotificationUnreadCount(queryClient, event.count);
    };

    socket.on('connect', handleConnect);
    socket.on(SOCKET_EVENTS.UNREAD_COUNT, handleUnread);
    socket.on(SOCKET_EVENTS.NOTIFICATION_COUNT, handleNotificationCount);

    return () => {
      socket.off('connect', handleConnect);
      socket.off(SOCKET_EVENTS.UNREAD_COUNT, handleUnread);
      socket.off(SOCKET_EVENTS.NOTIFICATION_COUNT, handleNotificationCount);
    };
  }, [queryClient, socket]);
}
